import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";
import { toast } from "react-toastify";
import "./NutritionMediaPicker.css";

const ACCEPTED = "image/jpeg,image/jpg,image/png,image/gif,image/webp";

export default function NutritionGallery() {
  const { state } = useContext(NoteState);
  const branch_id = state?.branch_id;
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const fileRef = useRef(null);

  const loadImages = useCallback(async () => {
    if (!branch_id) return;
    setLoading(true);
    try {
      const res = await UserServices.getNutritionGalleryImages({
        branch_id,
        search: search || undefined,
        limit: 200,
      });
      if (res.status === 200 && res.data?.status) {
        setImages(res.data.data || []);
      }
    } catch (err) {
      console.log(err);
      toast.error("Failed to load gallery");
    }
    setLoading(false);
  }, [branch_id, search]);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  const handleUpload = async (fileList) => {
    const files = [...(fileList || [])].filter(Boolean);
    if (!files.length) return;

    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`${f.name} exceeds 5MB limit`);
        return;
      }
    }

    setUploading(true);
    try {
      const res = await UserServices.uploadNutritionGalleryImages(branch_id, files);
      if (res.status === 200 || res.status === 201) {
        toast.success(res.data?.message || "Images uploaded");
        loadImages();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Upload failed");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this image from the gallery?")) return;
    try {
      const res = await UserServices.deleteNutritionGalleryImage(id);
      if (res.status === 200) {
        toast.success("Image removed");
        setImages((prev) => prev.filter((img) => img.id !== id));
      }
    } catch (err) {
      toast.error("Could not delete image");
    }
  };

  return (
    <div className="page-wrapper nutrition-gallery-page">
      <div className="page-content">
        <div className="card">
          <div className="card-body">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
              <div>
                <MenuButton />
                <h4 className="d-inline-block ms-2 mb-0">Nutrition Image Gallery</h4>
                <p className="text-muted small mb-0 mt-1">
                  Upload images once and reuse them when building meal plans.
                </p>
              </div>
              <div className="d-flex gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  className="d-none"
                  accept={ACCEPTED}
                  multiple
                  onChange={(e) => handleUpload(e.target.files)}
                />
                <ButtonUpload uploading={uploading} onClick={() => fileRef.current?.click()} />
              </div>
            </div>

            <div className="row g-2 mb-3">
              <div className="col-md-6">
                <input
                  type="search"
                  className="form-control"
                  placeholder="Search by name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadImages()}
                />
              </div>
              <div className="col-auto">
                <button type="button" className="btn btn-outline-secondary" onClick={loadImages}>
                  Search
                </button>
              </div>
            </div>

            {loading ? (
              <p className="text-center py-5 text-muted">Loading gallery...</p>
            ) : !branch_id ? (
              <div className="text-center py-5 text-muted">
                <p>Select a branch from the header to manage gallery images.</p>
              </div>
            ) : images.length === 0 ? (
              <div className="nutrition-media-upload-zone text-center py-5">
                <i className="bi bi-images fs-1 text-muted d-block mb-2" />
                <p className="text-muted">No images yet. Upload your first batch of meal photos.</p>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                >
                  {uploading ? "Uploading..." : "Upload images"}
                </button>
              </div>
            ) : (
              <div className="nutrition-media-grid">
                {images.map((img) => (
                  <div key={img.id} className="nutrition-gallery-card">
                    <button
                      type="button"
                      className="btn btn-sm btn-danger btn-delete"
                      title="Remove"
                      onClick={() => handleDelete(img.id)}
                    >
                      <i className="bi bi-trash" />
                    </button>
                    <a href={img.image_url} target="_blank" rel="noreferrer">
                      <img src={img.image_url} alt={img.title || "Gallery"} />
                    </a>
                    <div className="card-meta text-truncate" title={img.title || img.filename}>
                      {img.title || img.filename}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ButtonUpload({ uploading, onClick }) {
  return (
    <button type="button" className="btn btn-primary" disabled={uploading} onClick={onClick}>
      <i className="bi bi-cloud-upload me-1" />
      {uploading ? "Uploading..." : "Upload images"}
    </button>
  );
}
